import React from 'react';
import { useForm } from 'react-hook-form';
import { X, Calendar, Clock, User, Mail, Phone } from 'lucide-react';
import { dataService } from '../services/dataService';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  carId: string;
  carName: string;
}

interface TestDriveForm {
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
}

export const TestDriveModal: React.FC<ModalProps> = ({ isOpen, onClose, carId, carName }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<TestDriveForm>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  if (!isOpen) return null;

  const onSubmit = async (data: TestDriveForm) => {
    setIsSubmitting(true);
    try {
      await dataService.requestTestDrive({
        carId,
        carName,
        customerName: data.name,
        email: data.email,
        phone: data.phone,
        preferredDate: data.date,
        preferredTime: data.time
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        reset();
        onClose();
      }, 2000);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full border border-gray-700">
          <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl leading-6 font-medium text-white" id="modal-title">
                Schedule Test Drive
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-white focus:outline-none">
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Book a test drive for the <span className="text-primary font-semibold">{carName}</span>. We'll confirm via email shortly.
            </p>

            {success ? (
              <div className="p-4 bg-green-900/50 border border-green-500 rounded text-green-200 text-center">
                Request sent successfully!
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-500" />
                    </div>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      type="text"
                      className="bg-darker border border-gray-700 text-white rounded-md pl-10 w-full py-2 focus:ring-primary focus:border-primary"
                      placeholder="John Doe"
                    />
                  </div>
                  {errors.name && <span className="text-red-500 text-xs">{errors.name.message}</span>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        {...register('email', { required: 'Email is required' })}
                        type="email"
                        className="bg-darker border border-gray-700 text-white rounded-md pl-10 w-full py-2 focus:ring-primary focus:border-primary"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        {...register('phone', { required: 'Phone is required' })}
                        type="tel"
                        className="bg-darker border border-gray-700 text-white rounded-md pl-10 w-full py-2 focus:ring-primary focus:border-primary"
                        placeholder="(312) 555-0123"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Date</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Calendar className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        {...register('date', { required: 'Date is required' })}
                        type="date"
                        className="bg-darker border border-gray-700 text-white rounded-md pl-10 w-full py-2 focus:ring-primary focus:border-primary"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Time</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Clock className="h-5 w-5 text-gray-500" />
                      </div>
                      <select
                        {...register('time', { required: 'Time is required' })}
                        className="bg-darker border border-gray-700 text-white rounded-md pl-10 w-full py-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="">Select Time</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="17:00">5:00 PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Confirm Request'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-transparent text-base font-medium text-gray-300 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};